import { useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Webcam from 'react-webcam';

import { useReceiptAnalysis } from '../hooks/useReceiptAnalysis';
import type { ReceiptAnalysisResults } from '../types/receipt/receipt';
import { LoadingSpinner } from './LoadingSpinner';
import { Modal } from './Modal';

interface ReceiptUploadModalProps {
  onAnalysisComplete: (results: ReceiptAnalysisResults, receiptFile: File) => void;
  onCancel: () => void;
  onEnterManually?: (receiptFile?: File) => void;
}

type UploadMode = 'select' | 'upload' | 'camera';

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
const ACCEPTED_FILE_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'application/pdf': ['.pdf'],
};

function ReceiptUploadModal({
  onAnalysisComplete,
  onCancel,
  onEnterManually,
}: ReceiptUploadModalProps) {
  const [mode, setMode] = useState<UploadMode>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const webcamRef = useRef<Webcam>(null);

  const { mutate: analyzeReceipt, isPending: isAnalyzing } = useReceiptAnalysis();

  const convertBase64ToFile = (base64: string, filename: string): Promise<File> => {
    return fetch(base64)
      .then((res) => res.blob())
      .then((blob) => new File([blob], filename, { type: 'image/jpeg' }));
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0]?.code === 'file-too-large') {
          setUploadError('File size must be less than 3MB');
        } else if (rejection.errors[0]?.code === 'file-invalid-type') {
          setUploadError('Please upload a valid receipt (JPEG/PNG/PDF, max 3MB)');
        } else {
          setUploadError('Please upload a valid receipt (JPEG/PNG/PDF, max 3MB)');
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setSelectedFile(file);
        setUploadError(null);

        // Create preview for images
        if (file.type.startsWith('image/')) {
          const url = URL.createObjectURL(file);
          setPreviewUrl(url);
        } else {
          setPreviewUrl(null);
        }
      }
    },
  });

  const handleAnalyze = () => {
    if (!selectedFile) return;

    analyzeReceipt(
      { receiptFile: selectedFile },
      {
        onSuccess: (results) => {
          // The backend signals soft failures (Gemini rate limits, 5xx,
          // timeouts, malformed responses, etc.) with `analysisFailed: true`.
          // In that case skip the error UI and route the user straight to the
          // manual entry form, carrying the already-uploaded receipt along.
          if (results.analysisFailed) {
            if (onEnterManually) {
              onEnterManually(selectedFile ?? undefined);
              return;
            }
            setUploadError(
              'Unable to analyze receipt. Please try again or enter details manually.',
            );
            return;
          }
          onAnalysisComplete(results, selectedFile);
        },
        onError: (error) => {
          // Any unexpected failure (network error, 5xx, etc.) should also
          // divert to manual entry rather than blocking the user.
          if (onEnterManually) {
            onEnterManually(selectedFile ?? undefined);
            return;
          }
          setUploadError(
            error.message ||
              'Unable to analyze receipt. Please try again or enter details manually.',
          );
        },
      },
    );
  };

  const handleRetake = () => {
    setCapturedImage(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
  };

  const handleSwitchCamera = () => {
    setFacingMode((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  const handleChooseDifferentFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadError(null);
  };

  const handleBack = () => {
    setMode('select');
    setSelectedFile(null);
    setPreviewUrl(null);
    setCapturedImage(null);
    setUploadError(null);
  };

  const isCamera = mode === 'camera';
  const hasError = Boolean(uploadError);
  const showManualEntry = hasError && Boolean(onEnterManually);

  const secondaryLabel = isCamera
    ? 'Retake'
    : hasError
      ? 'Upload Different File'
      : 'Choose Different File';
  const secondaryAction = isCamera
    ? handleRetake
    : hasError
      ? handleBack
      : handleChooseDifferentFile;

  const primaryLabel = showManualEntry ? 'Enter Details Manually' : 'Analyze Receipt';
  const primaryAction =
    hasError && onEnterManually ? () => onEnterManually(selectedFile ?? undefined) : handleAnalyze;

  return (
    <Modal title="Scan Receipt" onClose={onCancel} maxWidth="2xl" closeOnEscape={!isAnalyzing}>
      <div className="space-y-6" data-testid="receipt-upload-modal">
        {mode === 'select' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Take a photo or upload your receipt to automatically extract claim details.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                data-testid="upload-receipt-option"
                onClick={() => setMode('upload')}
                className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 transition-colors hover:border-red-400 hover:bg-gray-50"
              >
                <svg
                  className="mb-3 size-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                <span className="text-lg font-medium text-gray-900">Upload Receipt</span>
                <span className="mt-1 text-sm text-gray-500">PDF, PNG, or JPG</span>
              </button>

              <button
                type="button"
                onClick={() => setMode('camera')}
                className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8 transition-colors hover:border-red-400 hover:bg-gray-50"
              >
                <svg
                  className="mb-3 size-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-lg font-medium text-gray-900">Take Photo</span>
                <span className="mt-1 text-sm text-gray-500">Use your camera</span>
              </button>
            </div>
          </div>
        )}

        {mode === 'upload' && !selectedFile && (
          <div className="space-y-4">
            <div
              {...getRootProps()}
              className={`cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
                isDragActive
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-300 hover:border-red-400 hover:bg-gray-50'
              }`}
            >
              <input {...getInputProps()} data-testid="receipt-file-input" />
              <svg
                className="mx-auto mb-4 size-16 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="mb-2 text-lg text-gray-700">
                {isDragActive ? 'Drop your receipt here' : 'Drag and drop your receipt'}
              </p>
              <p className="text-sm text-gray-500">or click to select a file</p>
              <p className="mt-2 text-xs text-gray-400">PDF, PNG, or JPG (max 3MB)</p>
            </div>
            {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            <button
              type="button"
              onClick={handleBack}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
          </div>
        )}

        {mode === 'camera' && !capturedImage && (
          <div className="space-y-4">
            <div
              className="relative overflow-hidden rounded-lg bg-black"
              style={{ aspectRatio: '4/3' }}
            >
              <Webcam
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: facingMode,
                }}
                className="size-full object-cover"
                ref={webcamRef}
              />
              <div className="absolute inset-x-0 bottom-4 flex items-center justify-center gap-4">
                <button
                  type="button"
                  onClick={handleSwitchCamera}
                  className="rounded-lg bg-white/90 px-4 py-2 font-medium text-gray-900 transition-colors hover:bg-white"
                >
                  Switch Camera
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const webcam = webcamRef.current;
                    if (webcam) {
                      const imageSrc = webcam.getScreenshot();
                      if (imageSrc) {
                        setCapturedImage(imageSrc);
                        void convertBase64ToFile(imageSrc, `receipt_${Date.now()}.jpg`).then(
                          (file) => {
                            setSelectedFile(file);
                            setPreviewUrl(imageSrc);
                          },
                        );
                      }
                    }
                  }}
                  className="rounded-full bg-red-600 px-8 py-3 font-medium text-white shadow-lg transition-colors hover:bg-red-700"
                >
                  Capture
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={handleBack}
              className="text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              ← Back
            </button>
          </div>
        )}

        {selectedFile && (
          <div className="flex max-h-[70vh] flex-col">
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
              {isAnalyzing && (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner />
                  <p className="ml-3 text-gray-600">Analyzing receipt...</p>
                </div>
              )}

              {previewUrl && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <img
                    src={previewUrl}
                    alt="Receipt preview"
                    className="h-full w-auto object-cover"
                  />
                </div>
              )}

              {!previewUrl && (
                <div className="rounded-lg border border-gray-200 p-6 text-center">
                  <svg
                    className="mx-auto mb-2 size-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="font-medium text-gray-700">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}

              {uploadError && (
                <div className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 p-3">
                  <svg
                    className="mt-0.5 size-4 shrink-0 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                    />
                  </svg>
                  <p className="text-sm text-red-700">{uploadError}</p>
                </div>
              )}
            </div>

            {!isAnalyzing && (
              <div className="flex shrink-0 gap-3 pt-4">
                <button
                  type="button"
                  onClick={secondaryAction}
                  className="flex-1 rounded-xl border border-gray-200 px-5 py-3 font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                >
                  {secondaryLabel}
                </button>
                <button
                  type="button"
                  onClick={primaryAction}
                  className="flex-1 rounded-xl bg-red-600 px-5 py-3 font-semibold text-white transition-all duration-150 hover:bg-red-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600 active:bg-red-700"
                >
                  {primaryLabel}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export { ReceiptUploadModal };
